import type { ModuleSearchResult } from '@/components/time-entry/LinkWorkSelector'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { EmptyVisual } from '@/components/shared/empty-states/EmptyVisual'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { TimeEntryTagging } from '@/components/time-entry/TimeEntryTagging'
import { Button } from '@/components/ui/button'
import {
  DIALOG_FIELD_AREA_CLASS,
  DIALOG_FIELD_CLASS,
} from '@/components/ui/dialog-form'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useSplitTimeEntryMutation } from '@/hooks/mutations/useTimeEntryMutations'
import { useTimeEntryQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { useEntityTerminology } from '@/hooks/useEntityTerminology'
import {
  timeEntryTagKeys,
  useReplaceTagsForTimeEntry,
} from '@/hooks/useTimeEntryTags'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { timeEntryTagApi } from '@/lib/api/time-entry-tags.api'
import { cn } from '@/lib/utils'
import { getModuleLinkMeta } from '@/modules/module-link'
import { CreateTimeEntryDialog } from '@/pages/time/_components/CreateTimeEntryDialog'
import { clockHourCycle } from '@/utils/date'
import { formatShortcutKeys } from '@/utils/keyboard-utils'
import {
  ArrowCounterClockwiseIcon,
  ArrowsSplitIcon,
  CircleNotchIcon,
  DotsThreeIcon,
  InfoIcon,
  NotePencilIcon,
  PencilSimpleIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useQueries } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  SplitTimeEntryInput,
  TimeEntry,
  TimeEntryLinkedModule,
} from '@scrambl/shared'
import {
  distributeTimeEntryMinutes,
  formatTimeEntryMinutes as formatMinutes,
  timeEntryMinutes as getEntryMinutes,
} from '@scrambl/shared'

interface SplitTimeEntryDialogProps {
  open: boolean
  entry: TimeEntry | null
  onOpenChange: (open: boolean) => void
  beforeSplit?: () => Promise<TimeEntry | null>
  onSplitComplete?: (entries: TimeEntry[]) => void
  requireCompletion?: boolean
  allowCancel?: boolean
  onCancel?: () => void
  totalMinutesOverride?: number
  linkedModulesOverride?: TimeEntryLinkedModule[]
  existingEntries?: TimeEntry[]
  allowedModuleTypes?: string[]
  pendingDescription?: string
  pendingDescriptionRequired?: boolean
  onPendingDescriptionChange?: (description: string) => void
  title?: string
  subtitle?: string
}

// 'even' edits raw minutes, 'hours' edits decimal hours (minutes stay the
// source of truth), 'percentage' edits shares of the total.
type SplitMode = 'even' | 'hours' | 'percentage'

const uniqueIds = (ids: Iterable<string>) => [...new Set(ids)]

/** Ids present in every list. Empty when any list is empty. */
const intersectIds = (lists: string[][]): string[] => {
  if (lists.length === 0) return []
  const [first, ...rest] = lists
  return uniqueIds(first).filter(id => rest.every(list => list.includes(id)))
}

const round = (value: number, decimals: number) => {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={450} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="More information"
          >
            <InfoIcon className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[240px]">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const formatEntryWindow = (entry: TimeEntry, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    hourCycle: clockHourCycle(),
    minute: '2-digit',
  })

  if (entry.startTime && entry.endTime) {
    return `${formatter.format(new Date(entry.startTime))} – ${formatter.format(new Date(entry.endTime))}`
  }

  if (entry.startTime) {
    return `${formatter.format(new Date(entry.startTime))} - now`
  }

  return 'Saved log'
}

export function SplitTimeEntryDialog({
  open,
  entry,
  onOpenChange,
  beforeSplit,
  onSplitComplete,
  requireCompletion = false,
  allowCancel = false,
  onCancel,
  totalMinutesOverride,
  linkedModulesOverride,
  existingEntries,
  allowedModuleTypes,
  pendingDescription,
  pendingDescriptionRequired = false,
  onPendingDescriptionChange,
  title = 'Finish Timer Session',
  subtitle = "Assign this session's time to the work it belongs to.",
}: SplitTimeEntryDialogProps) {
  const { singularWithArticle } = useEntityTerminology()
  const formId = useId()
  const { timezone } = useUserTimezone()
  const splitMutation = useSplitTimeEntryMutation()
  const replaceTagsMutation = useReplaceTagsForTimeEntry()
  const { data: entryDetails } = useTimeEntryQuery(entry?.id, {
    enabled: open && !!entry?.id,
  })
  const resolvedEntry = entryDetails ?? entry
  const resolvedLinkedModules = (() => {
    if (linkedModulesOverride?.length) {
      return linkedModulesOverride
    }
    if (entryDetails?.linkedModules?.length) {
      return entryDetails.linkedModules
    }
    return entry?.linkedModules ?? []
  })()

  const [splits, setSplits] = useState<SplitTimeEntryInput['splits']>([])
  const [percentages, setPercentages] = useState<number[]>([])
  const [splitMode, setSplitMode] = useState<SplitMode>('even')
  // In-progress decimal hours text per row, so typing "1." doesn't get
  // normalized away mid-keystroke. Cleared on blur and structural changes.
  const [hoursDrafts, setHoursDrafts] = useState<Record<number, string>>({})
  const [isPreparingSplit, setIsPreparingSplit] = useState(false)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [sessionEntriesDraft, setSessionEntriesDraft] = useState<TimeEntry[]>(
    [],
  )
  const [removedSavedIds, setRemovedSavedIds] = useState<Set<string>>(new Set())
  const [addTargetKey, setAddTargetKey] = useState(0)
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  const [expandedTaggingKey, setExpandedTaggingKey] = useState<string | null>(
    null,
  )

  const incomingMinutes = (() => {
    if (
      typeof totalMinutesOverride === 'number' &&
      Number.isFinite(totalMinutesOverride)
    ) {
      return Math.max(0, round(totalMinutesOverride, 6))
    }

    if (!resolvedEntry) return 0
    return getEntryMinutes(resolvedEntry)
  })()

  // Keep the allocation draft stable while the live timer advances or refetches.
  const allocationKey =
    open && resolvedEntry
      ? `${resolvedEntry.organizationId}:${resolvedEntry.id}`
      : null
  const [allocationClock, setAllocationClock] = useState({
    key: allocationKey,
    minutes: incomingMinutes,
  })
  if (
    allocationClock.key !== allocationKey ||
    (allocationClock.minutes === 0 && incomingMinutes > 0)
  )
    setAllocationClock({ key: allocationKey, minutes: incomingMinutes })
  const totalMinutes =
    allocationClock.key === allocationKey
      ? allocationClock.minutes
      : incomingMinutes

  const remainingMinutes = (() => {
    const allocated = splits.reduce(
      (sum, split) => sum + split.durationMinutes,
      0,
    )
    return round(totalMinutes - allocated, 6)
  })()

  const totalPercentage = (() => {
    return percentages.reduce((sum, pct) => sum + pct, 0)
  })()

  const percentageValid = (() => {
    return Math.abs(totalPercentage - 100) < 0.01
  })()

  const allocatedNewMinutes = (() => {
    if (splitMode === 'percentage') {
      return round(totalMinutes * (Math.min(totalPercentage, 100) / 100), 6)
    }
    return Math.min(totalMinutes, totalMinutes - remainingMinutes)
  })()

  const isOverAllocated =
    splitMode === 'percentage'
      ? totalPercentage - 100 > 0.01
      : remainingMinutes < 0

  const resolvedExistingEntries = (() => {
    return [...sessionEntriesDraft].sort((left, right) => {
      const leftTime = left.startTime ? new Date(left.startTime).getTime() : 0
      const rightTime = right.startTime
        ? new Date(right.startTime).getTime()
        : 0
      return rightTime - leftTime
    })
  })()

  const existingLoggedMinutes = (() => {
    return resolvedExistingEntries.reduce((sum, sessionEntry) => {
      if (removedSavedIds.has(sessionEntry.id)) return sum
      return sum + getEntryMinutes(sessionEntry)
    }, 0)
  })()

  const sessionTotalMinutes = totalMinutes + existingLoggedMinutes

  // Saved rows keep their tags on the server; read them here so the
  // "all allocations" field can show and edit them alongside the new rows.
  // Same key/fn as the per-row pickers, so the cache is shared.
  const savedTagQueries = useQueries({
    queries: resolvedExistingEntries.map(sessionEntry => ({
      queryKey: timeEntryTagKeys.activeByTimeEntry(sessionEntry.id),
      queryFn: () => timeEntryTagApi.getActiveTagsForTimeEntry(sessionEntry.id),
      enabled: open,
    })),
  })
  const savedTaggedUserIdsById = new Map(
    resolvedExistingEntries.map((sessionEntry, index) => [
      sessionEntry.id,
      (savedTagQueries[index]?.data?.data ?? []).map(tag => tag.taggedUserId),
    ]),
  )
  const activeSavedEntries = resolvedExistingEntries.filter(
    sessionEntry => !removedSavedIds.has(sessionEntry.id),
  )
  const sharedTaggedUserIds = intersectIds([
    ...splits.map(split => split.taggedUserIds ?? []),
    ...activeSavedEntries.map(
      sessionEntry => savedTaggedUserIdsById.get(sessionEntry.id) ?? [],
    ),
  ])
  const allocationCount = splits.length + activeSavedEntries.length

  const handleSharedTaggedUserIdsChange = async (userIds: string[]) => {
    const current = new Set(sharedTaggedUserIds)
    const next = new Set(userIds)
    const added = [...next].filter(id => !current.has(id))
    const removed = new Set([...current].filter(id => !next.has(id)))
    if (added.length === 0 && removed.size === 0) return

    const applyChange = (ids: string[]) =>
      uniqueIds([...ids.filter(id => !removed.has(id)), ...added])

    setSplits(prev =>
      prev.map(split => ({
        ...split,
        taggedUserIds: applyChange(split.taggedUserIds ?? []),
      })),
    )

    await Promise.all(
      activeSavedEntries.map(async sessionEntry => {
        try {
          await replaceTagsMutation.mutateAsync({
            timeEntryId: sessionEntry.id,
            data: {
              taggedUserIds: applyChange(
                savedTaggedUserIdsById.get(sessionEntry.id) ?? [],
              ),
            },
            suppressErrorToast: true,
          })
        } catch (error) {
          toast.error(
            getMutationErrorMessage(
              error,
              `Couldn't update the tagged members on ${sessionEntry.moduleName || 'a saved log'}.`,
            ),
          )
        }
      }),
    )
  }

  const toggleRemoveSaved = (id: string) => {
    setRemovedSavedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const getDisplayMinutes = (index: number): number => {
    if (splitMode !== 'percentage') return splits[index]?.durationMinutes ?? 0
    if (totalMinutes <= 0) return 0
    return round(totalMinutes * ((percentages[index] ?? 0) / 100), 6)
  }

  const minutesToHours = (minutes: number) => round(minutes / 60, 8)

  const getHoursInputValue = (index: number): string => {
    const draft = hoursDrafts[index]
    if (draft !== undefined) return draft
    const minutes = splits[index]?.durationMinutes ?? 0
    return minutes > 0 ? String(minutesToHours(minutes)) : ''
  }

  const getDisplayPercentage = (index: number): number => {
    if (splitMode === 'percentage') return percentages[index] ?? 0
    if (totalMinutes <= 0) return 0
    return round(
      ((splits[index]?.durationMinutes ?? 0) / totalMinutes) * 100,
      1,
    )
  }

  useEffect(() => {
    if (!open) lastSeedSignatureRef.current = null
    setRemovedSavedIds(new Set())
    setExpandedNotes(new Set())
    setExpandedTaggingKey(null)
  }, [allocationKey, open])
  useEffect(() => {
    setSessionEntriesDraft(existingEntries ?? [])
  }, [existingEntries, open])

  // Seed rows once per open and whenever the set of linked items changes.
  // The entry refetches after the timer stops, and re-seeding on that
  // identity change would wipe tags and minutes the user already picked.
  const seedSignature = open
    ? `${allocationKey ?? ''}|${totalMinutes > 0}|${resolvedLinkedModules
        .map(module => `${module.moduleType}:${module.moduleId}`)
        .join(',')}`
    : null
  const lastSeedSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (!resolvedEntry || !open) return
    if (lastSeedSignatureRef.current === seedSignature) return
    lastSeedSignatureRef.current = seedSignature

    setHoursDrafts({})

    if (resolvedLinkedModules.length === 0 || totalMinutes === 0) {
      setSplits([])
      setPercentages([])
      setSplitMode('even')
      return
    }

    const allocations = distributeTimeEntryMinutes(
      totalMinutes,
      resolvedLinkedModules.map(() => 1),
    )

    setSplits(
      resolvedLinkedModules.map((module, index) => ({
        moduleType: module.moduleType,
        moduleId: module.moduleId,
        moduleName: module.moduleName ?? null,
        durationMinutes: allocations[index],
        taggedUserIds: uniqueIds(module.taggedUserIds ?? []),
      })),
    )

    const evenPct = round(100 / resolvedLinkedModules.length, 2)
    setPercentages(
      resolvedLinkedModules.map((_, index) =>
        index === resolvedLinkedModules.length - 1
          ? round(100 - evenPct * (resolvedLinkedModules.length - 1), 2)
          : evenPct,
      ),
    )
    setSplitMode('even')
  }, [open, resolvedEntry, resolvedLinkedModules, totalMinutes, seedSignature])

  const handleMinutesChange = (index: number, value: string) => {
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : !Number.isFinite(Number(value)) ? 0 : Number(value)
    const minutes = Math.max(0, round(raw, 6))

    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleHoursChange = (index: number, value: string) => {
    setHoursDrafts(prev => ({ ...prev, [index]: value }))
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : !Number.isFinite(Number(value)) ? 0 : Number(value)
    const minutes = Math.max(0, round(raw * 60, 6))

    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleHoursBlur = (index: number) => {
    setHoursDrafts(prev => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const handleDescriptionChange = (index: number, value: string) => {
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, description: value } : split,
      ),
    )
  }

  const handleTaggedUserIdsChange = (index: number, userIds: string[]) => {
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, taggedUserIds: userIds } : split,
      ),
    )
  }

  const toggleTagging = (key: string) => {
    setExpandedTaggingKey(current => (current === key ? null : key))
  }

  const toggleNote = (key: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const handleFillRemaining = (index: number) => {
    if (remainingMinutes <= 0) return

    setHoursDrafts({})
    setSplits(prev =>
      prev.map((split, currentIndex) =>
        currentIndex === index
          ? {
              ...split,
              durationMinutes: split.durationMinutes + remainingMinutes,
            }
          : split,
      ),
    )
  }

  const handleSplitRemaining = () => {
    if (remainingMinutes <= 0 || splits.length === 0) return

    setHoursDrafts({})
    const blankIndexes = splits
      .map((split, index) =>
        !Number.isFinite(split.durationMinutes) || split.durationMinutes <= 0
          ? index
          : -1,
      )
      .filter(index => index >= 0)
    const targetIndexes =
      blankIndexes.length > 0 ? blankIndexes : splits.map((_, index) => index)
    const allocations = distributeTimeEntryMinutes(
      remainingMinutes,
      targetIndexes.map(() => 1),
    )

    setSplits(prev =>
      prev.map((split, index) => {
        const targetIndex = targetIndexes.indexOf(index)
        if (targetIndex === -1) return split
        return {
          ...split,
          durationMinutes: split.durationMinutes + allocations[targetIndex],
        }
      }),
    )
  }

  const handleAddSplitTarget = (result: ModuleSearchResult | null) => {
    if (!result) return

    const alreadyAdded = splits.some(
      split => split.moduleType === result.type && split.moduleId === result.id,
    )
    if (alreadyAdded) {
      toast.error('That item is already in this split')
      return
    }

    setHoursDrafts({})
    setSplits(prev => [
      ...prev,
      {
        moduleType:
          result.type as SplitTimeEntryInput['splits'][number]['moduleType'],
        moduleId: result.id,
        moduleName: result.name,
        durationMinutes: Math.max(0, remainingMinutes),
        taggedUserIds: [],
      },
    ])
    setAddTargetKey(key => key + 1)
  }

  const handlePercentageChange = (index: number, value: string) => {
    const trimmed = value.trim()
    const raw =
      trimmed === '' ? 0 : !Number.isFinite(Number(value)) ? 0 : Number(value)
    const pct = Math.max(0, round(raw, 2))

    setPercentages(prev =>
      prev.map((current, idx) => (idx === index ? pct : current)),
    )
    const minutes = round(totalMinutes * (pct / 100), 6)
    setSplits(prev =>
      prev.map((split, idx) =>
        idx === index ? { ...split, durationMinutes: minutes } : split,
      ),
    )
  }

  const handleModeChange = (value: string) => {
    if (!value || value === splitMode) return
    const nextMode = value as SplitMode
    setHoursDrafts({})

    if (nextMode === 'percentage') {
      if (totalMinutes > 0) {
        const nextPercentages = splits.map(split =>
          round((split.durationMinutes / totalMinutes) * 100, 2),
        )
        const total = nextPercentages.reduce((sum, pct) => sum + pct, 0)
        if (nextPercentages.length > 0) {
          nextPercentages[nextPercentages.length - 1] = round(
            nextPercentages[nextPercentages.length - 1] + (100 - total),
            2,
          )
        }
        setPercentages(nextPercentages)
      }
    } else if (
      // Minutes and hours share the same backing values — only recompute
      // minutes when actually leaving percentage mode.
      splitMode === 'percentage' &&
      totalMinutes > 0 &&
      percentages.length > 0
    ) {
      const nextMinutes = percentages.map(pct =>
        round(totalMinutes * (pct / 100), 6),
      )
      const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
      if (nextMinutes.length > 0) {
        nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
      }
      setSplits(prev =>
        prev.map((split, index) => ({
          ...split,
          durationMinutes: nextMinutes[index],
        })),
      )
    }

    setSplitMode(nextMode)
  }

  const handleEvenSplit = () => {
    if (!splits.length || totalMinutes <= 0) return

    setHoursDrafts({})
    if (splitMode !== 'percentage') {
      const allocations = distributeTimeEntryMinutes(
        totalMinutes,
        splits.map(() => 1),
      )
      setSplits(prev =>
        prev.map((split, index) => ({
          ...split,
          durationMinutes: allocations[index],
        })),
      )
      return
    }

    const evenPct = round(100 / splits.length, 2)
    const nextPercentages = splits.map((_, index) =>
      index === splits.length - 1
        ? round(100 - evenPct * (splits.length - 1), 2)
        : evenPct,
    )
    setPercentages(nextPercentages)

    const nextMinutes = nextPercentages.map(pct =>
      round(totalMinutes * (pct / 100), 6),
    )
    const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
    if (nextMinutes.length > 0) {
      nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
    }
    setSplits(prev =>
      prev.map((split, index) => ({
        ...split,
        durationMinutes: nextMinutes[index],
      })),
    )
  }

  const handleRemoveSplit = (index: number) => {
    setHoursDrafts({})
    setSplits(prev => prev.filter((_, currentIndex) => currentIndex !== index))
    setPercentages(prev => {
      const nextPercentages = prev.filter(
        (_, currentIndex) => currentIndex !== index,
      )

      if (nextPercentages.length === 0) {
        return []
      }

      const total = nextPercentages.reduce((sum, pct) => sum + pct, 0)
      if (total <= 0) {
        const evenPct = round(100 / nextPercentages.length, 2)
        return nextPercentages.map((_, currentIndex) =>
          currentIndex === nextPercentages.length - 1
            ? round(100 - evenPct * (nextPercentages.length - 1), 2)
            : evenPct,
        )
      }

      const normalized = nextPercentages.map(pct =>
        round((pct / total) * 100, 2),
      )
      const normalizedTotal = normalized.reduce((sum, pct) => sum + pct, 0)
      normalized[normalized.length - 1] = round(
        normalized[normalized.length - 1] + (100 - normalizedTotal),
        2,
      )
      return normalized
    })
  }

  const handleSubmit = async () => {
    if (!resolvedEntry || isPreparingSplit || splitMutation.isPending) return
    if (!splits.length) {
      toast.error('Add at least one linked item to split this entry')
      return
    }

    let finalSplits = splits.map(split => ({ ...split }))

    if (splitMode === 'percentage') {
      const nextMinutes = percentages.map(pct =>
        round(totalMinutes * (pct / 100), 6),
      )
      const allocated = nextMinutes.reduce((sum, minutes) => sum + minutes, 0)
      if (nextMinutes.length > 0) {
        nextMinutes[nextMinutes.length - 1] += totalMinutes - allocated
      }

      finalSplits = splits.map((split, index) => ({
        ...split,
        durationMinutes: nextMinutes[index],
      }))
    }

    if (
      finalSplits.some(
        split =>
          !Number.isFinite(split.durationMinutes) || split.durationMinutes <= 0,
      )
    ) {
      toast.error('Each split must have a positive duration')
      return
    }

    const totalAllocated = finalSplits.reduce(
      (sum, split) => sum + split.durationMinutes,
      0,
    )
    if (totalAllocated <= 0) {
      toast.error('Total split time must be greater than zero')
      return
    }

    if (splitMode === 'percentage' && !percentageValid) {
      toast.error('Total allocation must equal 100%')
      return
    }
    if (Math.abs(totalAllocated - totalMinutes) > 0.000001) {
      toast.error(
        'Assign the saved total exactly. Edit the total time separately to change it.',
      )
      return
    }
    let splitTarget = resolvedEntry

    const idsToDelete = [...removedSavedIds]

    setIsPreparingSplit(true)
    try {
      if (beforeSplit) {
        const preparedEntry = await beforeSplit()
        if (!preparedEntry) {
          toast.error('Unable to finalize this timer for splitting')
          return
        }
        splitTarget = preparedEntry
        // Stopping may add elapsed seconds or apply the workspace's rounding.
        // Preserve the user's proportions against the actual saved total.
        const savedAllocations = distributeTimeEntryMinutes(
          getEntryMinutes(preparedEntry),
          finalSplits.map(split => split.durationMinutes),
        )
        finalSplits = finalSplits.map((split, index) => ({
          ...split,
          durationMinutes: savedAllocations[index] ?? 0,
        }))
      }
    } catch (error) {
      toast.error(
        getMutationErrorMessage(error, "Couldn't finalize this timer."),
      )
      return
    } finally {
      setIsPreparingSplit(false)
    }

    splitMutation.mutate(
      {
        id: splitTarget.id,
        data: {
          splits: finalSplits,
          ...(idsToDelete.length ? { removeEntryIds: idsToDelete } : {}),
        },
      },
      {
        onSuccess: entries => {
          onSplitComplete?.(entries)
          onOpenChange(false)
        },
      },
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && requireCompletion && !allowCancel) {
      toast.error('Split this time entry to finish')
      return
    }
    if (!nextOpen && allowCancel) {
      onCancel?.()
    }
    onOpenChange(nextOpen)
  }

  const isSubmitDisabled =
    isPreparingSplit ||
    splitMutation.isPending ||
    splits.length === 0 ||
    (splitMode !== 'percentage' && Math.abs(remainingMinutes) > 0.000001) ||
    splits.some(
      split =>
        !Number.isFinite(split.durationMinutes) || split.durationMinutes <= 0,
    ) ||
    Boolean(
      pendingDescriptionRequired &&
      onPendingDescriptionChange &&
      !pendingDescription?.trim(),
    )

  const isReadyToSave =
    !isSubmitDisabled && (splitMode !== 'percentage' || percentageValid)

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
      event.preventDefault()
      if (
        isSubmitDisabled ||
        (splitMode === 'percentage' && !percentageValid)
      ) {
        return
      }
      void handleSubmit()
    }
  }

  const isFromLinkWorkDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-link-work-dropdown]')
  const isFromMemberTaggingDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-member-tagging-dropdown]')

  const footer = (
    <>
      {allowCancel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => handleOpenChange(false)}
        >
          Cancel
        </Button>
      ) : null}
      <Button
        type="button"
        size="sm"
        onClick={() => void handleSubmit()}
        disabled={
          isSubmitDisabled || (splitMode === 'percentage' && !percentageValid)
        }
        className="min-w-[140px]"
        title={
          isReadyToSave
            ? `${formatShortcutKeys(['Mod', 'D'])} to save`
            : undefined
        }
      >
        {isPreparingSplit || splitMutation.isPending ? (
          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
        ) : null}
        Save split
      </Button>
    </>
  )

  return (
    <>
      <DialogPattern
        open={open}
        onOpenChange={handleOpenChange}
        size="lg"
        title={title}
        description={subtitle}
        icon={
          <ArrowsSplitIcon className="h-4 w-4 text-primary" weight="duotone" />
        }
        hideCloseButton={requireCompletion && !allowCancel}
        contentProps={{
          onEscapeKeyDown: event => {
            if (document.querySelector('[data-member-tagging-dropdown]')) {
              event.preventDefault()
            }
          },
          onInteractOutside: event => {
            const outsideTarget = event.detail.originalEvent.target
            if (
              isFromLinkWorkDropdown(outsideTarget) ||
              isFromMemberTaggingDropdown(outsideTarget)
            ) {
              event.preventDefault()
            }
          },
          onPointerDownOutside: event => {
            const outsideTarget = event.detail.originalEvent.target
            if (
              isFromLinkWorkDropdown(outsideTarget) ||
              isFromMemberTaggingDropdown(outsideTarget)
            ) {
              event.preventDefault()
            }
          },
          onKeyDown: handleKeyDown,
        }}
        contentClassName="space-y-4"
        footer={footer}
      >
        <div className="rounded-md bg-muted/30 px-4 py-3.5">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-semibold tabular-nums tracking-tight">
                {formatMinutes(sessionTotalMinutes)}
              </span>
              <span className="text-xs text-muted-foreground">
                session total
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
              {existingLoggedMinutes > 0 ? (
                <span className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {formatMinutes(existingLoggedMinutes)} saved
                </span>
              ) : null}
              <span className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    isOverAllocated
                      ? 'bg-destructive'
                      : 'bg-[hsl(var(--button-primary))]',
                  )}
                />
                {formatMinutes(allocatedNewMinutes)} of{' '}
                {formatMinutes(totalMinutes)} assigned
              </span>
              <span>
                {resolvedExistingEntries.length + splits.length} allocation
                {resolvedExistingEntries.length + splits.length === 1
                  ? ''
                  : 's'}
              </span>
            </div>
          </div>
          {sessionTotalMinutes > 0 ? (
            <div className="mt-2.5 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
              {existingLoggedMinutes > 0 ? (
                <div
                  className="h-full bg-emerald-500 transition-[width] duration-300"
                  style={{
                    width: `${(existingLoggedMinutes / sessionTotalMinutes) * 100}%`,
                  }}
                />
              ) : null}
              {allocatedNewMinutes > 0 ? (
                <div
                  className={cn(
                    'h-full transition-[width] duration-300',
                    isOverAllocated
                      ? 'bg-destructive'
                      : 'bg-[hsl(var(--button-primary))]',
                    existingLoggedMinutes > 0 && 'ml-px',
                  )}
                  style={{
                    width: `${(Math.max(0, allocatedNewMinutes) / sessionTotalMinutes) * 100}%`,
                  }}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          <div>
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-1.5 text-sm font-semibold">
                Time allocations
                <InfoHint text="Saved logs and new splits live in one list. Edit, remove, or add — then Save." />
              </div>

              <div className="flex items-center gap-2 sm:justify-end">
                <Tabs
                  value={splitMode}
                  onValueChange={handleModeChange}
                  className="flex-1 sm:flex-none"
                >
                  <TabsList variant="pill" className="grid w-full grid-cols-3">
                    <TabsTrigger value="even" className="text-xs">
                      Minutes
                    </TabsTrigger>
                    <TabsTrigger value="hours" className="text-xs">
                      Hours
                    </TabsTrigger>
                    <TabsTrigger value="percentage" className="text-xs">
                      Percentage
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 flex-shrink-0 gap-1.5 px-3 text-xs"
                    >
                      <DotsThreeIcon className="h-4 w-4" weight="bold" />
                      Actions
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem
                      onClick={handleEvenSplit}
                      disabled={splits.length === 0}
                    >
                      Distribute evenly
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleSplitRemaining}
                      disabled={remainingMinutes <= 0 || splits.length === 0}
                    >
                      Split remaining
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {onPendingDescriptionChange ? (
              <div className="mb-4 border-b border-border/40 pb-4">
                <div className="flex items-center gap-1.5">
                  <label
                    htmlFor={`${formId}-remaining-timer-description`}
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Remaining timer description
                    {pendingDescriptionRequired ? (
                      <span className="text-destructive"> *</span>
                    ) : null}
                  </label>
                  <InfoHint text="Description saved on the remaining timer entry that will be split." />
                </div>
                <Textarea
                  id={`${formId}-remaining-timer-description`}
                  value={pendingDescription ?? ''}
                  onChange={event =>
                    onPendingDescriptionChange(event.target.value)
                  }
                  placeholder="What were you working on?"
                  className={cn(
                    DIALOG_FIELD_AREA_CLASS,
                    'mt-2 min-h-[60px] resize-none text-sm',
                  )}
                />
              </div>
            ) : null}

            <div className="mb-4 rounded-lg border border-border/60 bg-muted/20 p-3">
              <TimeEntryTagging
                label={
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      Tag members on all allocations
                    </span>
                    <InfoHint text="Adds the member to every allocation below, saved and new. Use the people icon on a row to tag someone on that row only." />
                  </div>
                }
                selectedUserIds={sharedTaggedUserIds}
                onSelectedUserIdsChange={userIds =>
                  void handleSharedTaggedUserIdsChange(userIds)
                }
                excludedUserIds={
                  resolvedEntry?.userId ? [resolvedEntry.userId] : undefined
                }
                hideHelperText
                closeOnScroll={false}
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">
                Applies to {allocationCount} allocation
                {allocationCount === 1 ? '' : 's'}.
              </p>
            </div>

            <div className="divide-y divide-border/60">
              <div className="hidden items-center justify-between px-1 pb-2 sm:flex">
                <span className="text-[11px] font-medium text-muted-foreground">
                  Link to
                </span>
                <div className="flex items-center gap-2">
                  <span className="w-[72px] text-right text-[11px] font-medium text-muted-foreground">
                    {splitMode === 'hours' ? 'Time (h)' : 'Time (min)'}
                  </span>
                  <span className="w-[72px] text-right text-[11px] font-medium text-muted-foreground">
                    Share %
                  </span>
                  <span className="w-[64px]" aria-hidden />
                  <span className="w-7" aria-hidden />
                  <span className="w-7" aria-hidden />
                  <span className="w-7" aria-hidden />
                </div>
              </div>

              {resolvedExistingEntries.map(sessionEntry => {
                const meta = getModuleLinkMeta(sessionEntry.moduleType)
                const Icon = meta.icon
                const isRemoved = removedSavedIds.has(sessionEntry.id)
                const taggingKey = `saved-${sessionEntry.id}`
                const taggingOpen = expandedTaggingKey === taggingKey
                const minutes = getEntryMinutes(sessionEntry)
                const pct =
                  sessionTotalMinutes > 0 && !isRemoved
                    ? round((minutes / sessionTotalMinutes) * 100, 1)
                    : 0

                return (
                  <div
                    key={sessionEntry.id}
                    className={cn('px-1 py-2.5', isRemoved && 'opacity-50')}
                  >
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div
                        className={cn(
                          'flex items-center gap-2 truncate text-sm font-medium sm:max-w-[260px]',
                          isRemoved && 'line-through',
                        )}
                      >
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                aria-label="Saved"
                                className="h-2 w-2 flex-shrink-0 rounded-full bg-emerald-500"
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              Saved log
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div
                          className={cn(
                            'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
                            meta.bgClass,
                          )}
                        >
                          <Icon className={cn('h-3.5 w-3.5', meta.textClass)} />
                        </div>
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">
                            {sessionEntry.moduleName || sessionEntry.moduleType}
                          </span>
                          <span
                            className={cn(
                              'truncate text-[11px] text-muted-foreground',
                              isRemoved && 'text-destructive/80',
                            )}
                          >
                            {formatEntryWindow(sessionEntry, timezone)} ·{' '}
                            {isRemoved ? 'will be deleted' : 'saved'}
                          </span>
                        </div>
                      </div>

                      <TooltipProvider delayDuration={300}>
                        <div className="flex flex-wrap items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                <span className="text-sm tabular-nums text-muted-foreground">
                                  {splitMode === 'hours'
                                    ? minutesToHours(minutes)
                                    : minutes}
                                </span>
                                <span className="text-xs text-muted-foreground sm:hidden">
                                  {splitMode === 'hours' ? 'h' : 'min'}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              Saved duration · use the pencil to edit
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                <span className="text-sm tabular-nums text-muted-foreground">
                                  {pct}
                                </span>
                                <span className="text-xs text-muted-foreground sm:hidden">
                                  %
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              Share of session total
                            </TooltipContent>
                          </Tooltip>

                          <div className="flex justify-end sm:w-[64px]">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              onClick={() => setEditingEntry(sessionEntry)}
                              aria-label="Edit saved log"
                              disabled={isRemoved}
                            >
                              <PencilSimpleIcon className="h-3.5 w-3.5" />
                            </Button>
                          </div>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'h-7 w-7 text-muted-foreground hover:text-foreground',
                                  taggingOpen && 'bg-muted text-foreground',
                                )}
                                onClick={() => toggleTagging(taggingKey)}
                                aria-label={`Tag team for ${sessionEntry.moduleName || sessionEntry.moduleType}`}
                                aria-expanded={taggingOpen}
                                disabled={isRemoved}
                              >
                                <UsersIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              Add or edit tagged team
                            </TooltipContent>
                          </Tooltip>

                          {/* spacer to align with the new-row note slot */}
                          <span
                            className="hidden h-7 w-7 sm:block"
                            aria-hidden
                          />

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'h-7 w-7 text-muted-foreground',
                                  isRemoved
                                    ? 'hover:text-foreground'
                                    : 'hover:text-destructive',
                                )}
                                onClick={() =>
                                  toggleRemoveSaved(sessionEntry.id)
                                }
                                aria-label={
                                  isRemoved
                                    ? 'Restore saved log'
                                    : `Remove ${sessionEntry.moduleName || 'log'}`
                                }
                              >
                                {isRemoved ? (
                                  <ArrowCounterClockwiseIcon className="h-3.5 w-3.5" />
                                ) : (
                                  <XIcon className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {isRemoved ? 'Restore' : 'Delete saved log'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    </div>
                    {taggingOpen ? (
                      <div className="mt-2 rounded-lg border border-border/60 bg-muted/20 p-3 sm:ml-8">
                        <TimeEntryTagging
                          timeEntryId={sessionEntry.id}
                          label="Tag team (optional)"
                          hideHelperText
                          closeOnScroll={false}
                        />
                      </div>
                    ) : null}
                  </div>
                )
              })}

              {splits.map((split, index) => {
                const splitKey = `${split.moduleType}-${split.moduleId}`
                const taggingKey = `new-${splitKey}`
                const taggingOpen = expandedTaggingKey === taggingKey
                const noteOpen =
                  expandedNotes.has(splitKey) || Boolean(split.description)
                return (
                  <div key={splitKey} className="px-1 py-2.5">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2 truncate text-sm font-medium sm:max-w-[260px]">
                        <TooltipProvider delayDuration={300}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                aria-label="New"
                                className="h-2 w-2 flex-shrink-0 rounded-full border border-muted-foreground/40 bg-transparent"
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              New allocation
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {(() => {
                          const meta = getModuleLinkMeta(split.moduleType)
                          const Icon = meta.icon
                          return (
                            <div
                              className={cn(
                                'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
                                meta.bgClass,
                              )}
                            >
                              <Icon
                                className={cn('h-3.5 w-3.5', meta.textClass)}
                              />
                            </div>
                          )
                        })()}
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">
                            {split.moduleName || split.moduleType}
                          </span>
                          <span className="truncate text-[11px] text-muted-foreground">
                            {resolvedEntry
                              ? `${formatEntryWindow(resolvedEntry, timezone)} · new`
                              : 'new allocation'}
                          </span>
                        </div>
                      </div>

                      <TooltipProvider delayDuration={300}>
                        <div className="flex flex-wrap items-center gap-2">
                          {splitMode === 'even' ? (
                            <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                              <Input
                                type="number"
                                min={0}
                                className={cn(
                                  DIALOG_FIELD_CLASS,
                                  'h-8 w-16 text-right text-sm tabular-nums',
                                )}
                                aria-label={`Minutes for ${split.moduleName || split.moduleType}`}
                                value={
                                  split.durationMinutes === 0
                                    ? ''
                                    : split.durationMinutes
                                }
                                onChange={e =>
                                  handleMinutesChange(index, e.target.value)
                                }
                              />
                              <span className="text-xs text-muted-foreground sm:hidden">
                                min
                              </span>
                            </div>
                          ) : splitMode === 'hours' ? (
                            <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                              <Input
                                type="number"
                                min={0}
                                step="any"
                                className={cn(
                                  DIALOG_FIELD_CLASS,
                                  'h-8 w-16 text-right text-sm tabular-nums',
                                )}
                                aria-label={`Hours for ${split.moduleName || split.moduleType}`}
                                value={getHoursInputValue(index)}
                                onChange={e =>
                                  handleHoursChange(index, e.target.value)
                                }
                                onBlur={() => handleHoursBlur(index)}
                              />
                              <span className="text-xs text-muted-foreground sm:hidden">
                                h
                              </span>
                            </div>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                  <span className="text-sm tabular-nums text-muted-foreground">
                                    {getDisplayMinutes(index)}
                                  </span>
                                  <span className="text-xs text-muted-foreground sm:hidden">
                                    min
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Calculated from percentage
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {splitMode === 'percentage' ? (
                            <div className="flex items-center justify-end gap-1 sm:w-[72px]">
                              <Input
                                type="number"
                                min={0}
                                step="any"
                                className={cn(
                                  DIALOG_FIELD_CLASS,
                                  'h-8 w-16 text-right text-sm tabular-nums',
                                )}
                                aria-label={`Percentage for ${split.moduleName || split.moduleType}`}
                                value={
                                  percentages[index] === 0
                                    ? ''
                                    : percentages[index]
                                }
                                onChange={e =>
                                  handlePercentageChange(index, e.target.value)
                                }
                              />
                              <span className="text-xs text-muted-foreground sm:hidden">
                                %
                              </span>
                            </div>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-baseline justify-end gap-1 sm:w-[72px]">
                                  <span className="text-sm tabular-nums text-muted-foreground">
                                    {getDisplayPercentage(index)}
                                  </span>
                                  <span className="text-xs text-muted-foreground sm:hidden">
                                    %
                                  </span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Calculated from minutes
                              </TooltipContent>
                            </Tooltip>
                          )}

                          <div className="flex justify-end sm:w-[64px]">
                            {splitMode !== 'percentage' ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                                onClick={() => handleFillRemaining(index)}
                                disabled={remainingMinutes <= 0}
                              >
                                Fill rest
                              </Button>
                            ) : null}
                          </div>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'relative h-7 w-7 text-muted-foreground hover:text-foreground',
                                  (taggingOpen ||
                                    (split.taggedUserIds?.length ?? 0) > 0) &&
                                    'bg-muted text-foreground',
                                )}
                                onClick={() => toggleTagging(taggingKey)}
                                aria-label={`Tag team for ${split.moduleName || split.moduleType}`}
                                aria-expanded={taggingOpen}
                              >
                                <UsersIcon className="h-4 w-4" />
                                {(split.taggedUserIds?.length ?? 0) > 0 ? (
                                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--button-primary))] px-1 text-[9px] font-semibold leading-none text-[hsl(var(--button-primary-foreground))]">
                                    {split.taggedUserIds?.length}
                                  </span>
                                ) : null}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {(split.taggedUserIds?.length ?? 0) > 0
                                ? 'Edit tagged team'
                                : 'Tag team'}
                            </TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  'h-7 w-7 text-muted-foreground',
                                  noteOpen && 'text-foreground',
                                )}
                                onClick={() => toggleNote(splitKey)}
                                aria-label={
                                  noteOpen ? 'Hide note' : 'Add a note'
                                }
                                aria-pressed={noteOpen}
                              >
                                <NotePencilIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {split.description ? 'Edit note' : 'Add a note'}
                            </TooltipContent>
                          </Tooltip>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveSplit(index)}
                            aria-label={`Remove ${split.moduleName || split.moduleType}`}
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TooltipProvider>
                    </div>
                    {taggingOpen ? (
                      <div className="rounded-card mt-2 border border-border/60 bg-muted/20 p-3 sm:ml-8">
                        <TimeEntryTagging
                          label="Tag team (optional)"
                          selectedUserIds={split.taggedUserIds ?? []}
                          onSelectedUserIdsChange={userIds =>
                            handleTaggedUserIdsChange(index, userIds)
                          }
                          hideHelperText
                          closeOnScroll={false}
                        />
                      </div>
                    ) : null}
                    {noteOpen ? (
                      <div className="mt-2 pl-8">
                        <Textarea
                          value={split.description ?? ''}
                          aria-label={`Description for ${split.moduleName || split.moduleType}`}
                          onChange={event =>
                            handleDescriptionChange(index, event.target.value)
                          }
                          placeholder="Add a description for this item (optional)"
                          className={cn(
                            DIALOG_FIELD_AREA_CLASS,
                            'min-h-[56px] resize-none text-sm',
                          )}
                        />
                      </div>
                    ) : null}
                  </div>
                )
              })}

              {resolvedExistingEntries.length === 0 && splits.length === 0 ? (
                <EmptyVisual
                  size="card"
                  shape="rows"
                  line="No allocations yet"
                  hint="Search below to add one."
                />
              ) : null}
            </div>

            <div className="mt-3 border-t border-border/40 pt-3">
              <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <PlusIcon className="h-3.5 w-3.5" />
                Add allocation
                <InfoHint
                  text={`Search for ${singularWithArticle}, project, task, or task group to allocate the remaining timer to.`}
                />
              </div>
              <LinkWorkSelector
                key={addTargetKey}
                value={null}
                onSelect={handleAddSplitTarget}
                allowedModuleTypes={allowedModuleTypes}
                defaultModule="ALL"
                portalToBody
                closeOnScroll={false}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-3 text-sm">
            <div className="text-muted-foreground">Remaining to assign</div>
            <div
              className={cn(
                'font-semibold tabular-nums',
                (
                  splitMode === 'percentage'
                    ? percentageValid
                    : remainingMinutes === 0
                )
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : isOverAllocated
                    ? 'text-destructive'
                    : 'text-amber-600 dark:text-amber-400',
              )}
            >
              {splitMode === 'percentage'
                ? `${round(100 - totalPercentage, 2)}%`
                : splitMode === 'hours'
                  ? `${minutesToHours(remainingMinutes)} h`
                  : `${remainingMinutes} min`}
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Splitting keeps the saved total without rounding it again. Timer
          ranges and total paused time are divided proportionally. Edit the
          total separately to change it.
        </p>

        {/* Validation status reads with the form it describes; the shell's
              footer stays buttons only. */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <span
            className={cn(
              'h-1.5 w-1.5 flex-shrink-0 rounded-full',
              isReadyToSave ? 'bg-emerald-500' : 'bg-amber-500',
            )}
          />
          {splitMode === 'percentage' && !percentageValid ? (
            <span>Total allocation must equal 100%</span>
          ) : pendingDescriptionRequired &&
            onPendingDescriptionChange &&
            !pendingDescription?.trim() ? (
            <span>Add a description for the remaining timer</span>
          ) : remainingMinutes !== 0 && splitMode !== 'percentage' ? (
            <span>All time must be assigned before saving</span>
          ) : (
            <span>Ready to save</span>
          )}
        </div>
      </DialogPattern>

      <CreateTimeEntryDialog
        open={!!editingEntry}
        entry={editingEntry}
        disableNestedSplitDialog
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setEditingEntry(null)
          }
        }}
        onSaved={updatedEntry => {
          setSessionEntriesDraft(prev =>
            prev.map(sessionEntry =>
              sessionEntry.id === updatedEntry.id ? updatedEntry : sessionEntry,
            ),
          )
          setEditingEntry(null)
        }}
      />
    </>
  )
}
